import React from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  SimpleGrid,
  Badge,
  Divider,
  Link,
  Icon,
  useColorModeValue,
  Card,
  CardBody,
  CardHeader,
} from '@chakra-ui/react';
import { EmailIcon, PhoneIcon } from '@chakra-ui/icons';

const TechStack = () => {
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  const techStackItems = [
    {
      category: 'Frontend',
      technologies: [
        { name: 'React', version: '19.1.0', description: 'User interface library' },
        { name: 'TypeScript', version: '4.9.5', description: 'Type-safe JavaScript' },
        { name: 'Chakra UI', version: '2.8.2', description: 'Component library' },
        { name: 'React Router', version: '7.5.0', description: 'Client-side routing' },
      ]
    },
    {
      category: 'Backend & Cloud',
      technologies: [
        { name: 'AWS Lambda', version: 'Latest', description: 'Serverless compute' },
        { name: 'AWS DynamoDB', version: 'Latest', description: 'NoSQL database' },
        { name: 'AWS API Gateway', version: 'Latest', description: 'API management' },
        { name: 'Node.js', version: '18.x', description: 'Server runtime' },
      ]
    },
    {
      category: 'Services & Integrations',
      technologies: [
        { name: 'Brevo (Sendinblue)', version: '2.2.0', description: 'Email service' },
        { name: 'Google Places API', version: 'Latest', description: 'Restaurant data' },
        { name: 'HTTPS/SSL', version: 'TLS 1.3', description: 'Secure connections' },
      ]
    },
    {
      category: 'Development & Deployment',
      technologies: [
        { name: 'AWS Amplify', version: 'Latest', description: 'CI/CD and hosting platform' },
        { name: 'Git', version: 'Latest', description: 'Version control' },
        { name: 'AWS CloudFront', version: 'Latest', description: 'CDN for global distribution' },
      ]
    }
  ];

  return (
    <Box minH="100vh" bg="mamaFuel.cream">
        
      <Container maxW="container.xl" py="10" px={{ base: "4", md: "6" }}>
        <VStack spacing="8" align="stretch">
          
          {/* Header */}
          <Box textAlign="center">
            <Heading size="xl" color="mamaFuel.text.primary" >
              MommaFuel Tech Stack
            </Heading>
          </Box>

          {/* Owner Information */}
          <Card bg={bgColor} borderColor={borderColor} borderWidth="1px" align="center">
            <CardBody>
              <VStack align="start" spacing="4">
                <Box>
                  <Heading size="md" color="mamaFuel.moss.700" mb="2">
                    Jonah Adler
                  </Heading>
                  <Text color="gray.600" mb="4">
                    Full-Stack Engineer & Founder
                  </Text>
                  
                  <HStack spacing="8" wrap="wrap">
                    <HStack spacing="2">
                      <Icon as={EmailIcon} color="mamaFuel.moss.700" />
                      <Link href="mailto:jonahadler5@gmail.com" color="mamaFuel.moss.700">
                        jonahadler5@gmail.com
                      </Link>
                    </HStack>
                    
                    <HStack spacing="2">
                      <Icon as={PhoneIcon} color="mamaFuel.moss.700" />
                      <Link href="tel:5712532286" color="mamaFuel.moss.700">
                        (571) 253-2286
                      </Link>
                    </HStack>
                  </HStack>
                </Box>
              </VStack>
            </CardBody>
          </Card>

          {/* Tech Stack Section */}
          <Card bg={bgColor} borderColor={borderColor} borderWidth="1px">
            <CardHeader>
              <Heading size="lg" color="mamaFuel.text.primary">
                🛠️ Technology Stack
              </Heading>
              <Text color="gray.600" mt="2">
                MommaFuel is built with modern, scalable technologies to ensure reliability and performance.
              </Text>
            </CardHeader>
            <CardBody>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing="6">
                {techStackItems.map((category) => (
                  <Box key={category.category}>
                    <Heading size="md" color="mamaFuel.moss.700" mb="4">
                      {category.category}
                    </Heading>
                    <VStack align="stretch" spacing="3">
                      {category.technologies.map((tech) => (
                        <Box key={tech.name} p="3" borderRadius="md" bg="gray.50">
                          <HStack justify="space-between" mb="1">
                            <Text fontWeight="bold">{tech.name}</Text>
                            <Badge colorScheme="green" variant="subtle">
                              {tech.version}
                            </Badge>
                          </HStack>
                          <Text fontSize="sm" color="gray.600">
                            {tech.description}
                          </Text>
                        </Box>
                      ))}
                    </VStack>
                  </Box>
                ))}
              </SimpleGrid>
            </CardBody>
          </Card>

          {/* Architecture Overview */}
          <Card bg={bgColor} borderColor={borderColor} borderWidth="1px" hidden>
            <CardHeader>
              <Heading size="lg" color="mamaFuel.text.primary">
                🏗️ System Architecture
              </Heading>
            </CardHeader>
            <CardBody>
              <VStack align="start" spacing="4">
                <Text>
                  MommaFuel uses a hybrid architecture combining React frontend with AWS serverless backend 
                  and traditional EC2 hosting for reliability and cost-effectiveness:
                </Text>
                
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing="6" width="full">
                  <Box>
                    <Heading size="sm" color="mamaFuel.moss.700" mb="2">
                      Frontend (Client-Side)
                    </Heading>
                    <Text fontSize="sm" color="gray.600">
                      React SPA built with Create React App, using TypeScript for type safety 
                      and Chakra UI for consistent, accessible components. Hosted on AWS Amplify with CloudFront CDN.
                    </Text>
                  </Box>
                  
                  <Box>
                    <Heading size="sm" color="mamaFuel.moss.700" mb="2">
                      Backend (Serverless)
                    </Heading>
                    <Text fontSize="sm" color="gray.600">
                      AWS Lambda functions handle all business logic, with DynamoDB for 
                      data persistence and API Gateway for request routing.
                    </Text>
                  </Box>
                  
                  <Box>
                    <Heading size="sm" color="mamaFuel.moss.700" mb="2">
                      Data Storage
                    </Heading>
                    <Text fontSize="sm" color="gray.600">
                      DynamoDB provides fast, scalable NoSQL storage for registries, 
                      meal signups, and user data with automatic scaling.
                    </Text>
                  </Box>
                  
                  <Box>
                    <Heading size="sm" color="mamaFuel.moss.700" mb="2">
                      Deployment
                    </Heading>
                    <Text fontSize="sm" color="gray.600">
                      Git-based CI/CD pipeline with AWS Amplify for automatic builds and deployments. 
                      Global distribution via CloudFront CDN for optimal performance.
                    </Text>
                  </Box>
                </SimpleGrid>
              </VStack>
            </CardBody>
          </Card>

          {/* Footer */}
          <Box textAlign="center" pt="1">
            <Divider mb="6" />
            <Text color="gray.500" fontSize="xs" mt="2">
            © {new Date().getFullYear()} MommaFuel. All technologies and trademarks are property of their respective owners.
            </Text>
          </Box>
        </VStack>
      </Container>
    </Box>
  );
};

export default TechStack;